import {ILocale} from 'data/country';
import moment from 'moment-timezone';

/**
 * 언어에 따라 날짜 표기를 변환합니다.
 *
 * 한국어 => 2024년 1월 31일
 *
 * 그 외 언어 => January 31, 2024
 *
 * @group Events
 * @event
 * @param locale - 세팅된 언어
 * @param date - (optional) 날짜, 기본값은 오늘 날짜
 * @returns 포멧된 날짜값
 *
 * @example
 * ```typescript
 * const locale = useRecoilValue(localeState);
 * const result = formatDateLanguage(locale);
 * // result: '2024년 1월 31일'
 * ```
 */

export const formatDateLanguage = (
  locale: ILocale,
  date: Date | string = new Date(),
) => {
  return moment(date).format(
    locale === 'ko-KR' ? 'YYYY년 MM월 DD일' : 'MMMM D, YYYY',
  );
};

/**
 * 카드 번호의 표기를 변환합니다.
 *
 * 1234342434235789 => 1234 **** **** 5789
 *
 * @group Events
 * @event
 * @param cardNumber - 카드번호
 *
 * @example
 * ```typescript
 * const result = formatCardNumber('1234342434235789');
 * // result: '1234 **** **** 5789'
 * ```
 */

export const formatCardNumber = (cardNumber: string) => {
  // 정규 표현식을 사용하여 숫자 이외의 문자 제거
  const cleanedNumber = cardNumber.replace(/\D/g, '');

  // 숫자를 4자리씩 끊어서 배열로 변환
  const formattedNumber = cleanedNumber.match(/.{1,4}/g) as string[];

  // 배열을 문자열로 합치면서 " **** "로 구분
  formattedNumber.splice(1, 2, '****', '****');
  return formattedNumber.join(' ');
};

/**
 * 사업자 등록 번호의 표기를 변환합니다.
 *
 * 1234567890 => 123-45-67890
 *
 * @group Events
 * @event
 * @param businessRegisterNumber - 사업자 등록 번호
 *
 * @example
 * ```typescript
 * const result = formatBusinessRegisterNumber('1234567890');
 * // result: '123-45-67890'
 * ```
 */

export const formatBusinessRegisterNumber = (
  businessRegisterNumber: string | undefined,
) => {
  if (businessRegisterNumber && businessRegisterNumber?.length > 10) {
    return businessRegisterNumber;
  }
  if (businessRegisterNumber) {
    businessRegisterNumber = businessRegisterNumber.replace(/[^0-9]/g, '');
    let tmp = '';
    if (businessRegisterNumber.length < 4) {
      return businessRegisterNumber;
    } else if (businessRegisterNumber.length < 6) {
      tmp += businessRegisterNumber.substr(0, 3);
      tmp += '-';
      tmp += businessRegisterNumber.substr(3);
      return tmp;
    } else if (businessRegisterNumber.length < 11) {
      tmp += businessRegisterNumber.substr(0, 3);
      tmp += '-';
      tmp += businessRegisterNumber.substr(3, 2);
      tmp += '-';
      tmp += businessRegisterNumber.substr(5);
      return tmp;
    }
  } else {
    return '';
  }
  return businessRegisterNumber;
};
